import { stripHtml } from "../../../utils/stringUtils";

// The pure side of a VOICE-mode ("Modern") survey (Survey.respond_mode ===
// "voice"): which screens the respondent walks through, and what each answer
// saves as.
//
// The steps are the survey's own, in the survey's own order, split into stages:
// - every OPEN_TEXT question gets a screen of its own (an implicit page break
//   before and after it): it is read aloud and answered by voice or typing. A
//   run of them is one "voice" stage, one question per screen.
// - everything else is a "page" stage, cut at the author's PAGE_BREAKs as in a
//   traditional survey: choice / Likert questions answered on screen, and
//   content-only TEXT steps, which can be read aloud on request.
// Empty pages (two breaks in a row, a break next to an open-text) are dropped.
//
// Steps are RespondSurveyPage's adapted shape: { id, type (UPPERCASE),
// question (HTML), is_required, choices: [{ id, text }] }.

const FORM_TYPES = new Set(["SINGLE_CHOICE", "MULTI_CHOICE", "LIKERT_SCALE"]);

export const isVoiceQuestion = (step) => step?.type === "OPEN_TEXT";
export const isContent = (step) => step?.type === "TEXT";
const isFormQuestion = (step) => FORM_TYPES.has(step?.type);

// [{ kind: "voice", questions }] | [{ kind: "page", items, questions }], where a
// page's `items` are its questions and content in order and `questions` only
// the answerable ones.
export function buildVoiceStages(steps) {
    const stages = [];
    let page = null;
    const closePage = () => {
        if (page && page.items.length > 0) stages.push(page);
        page = null;
    };
    for (const step of steps || []) {
        if (step?.type === "PAGE_BREAK") {
            closePage();
        } else if (isVoiceQuestion(step)) {
            closePage();
            const last = stages[stages.length - 1];
            if (last && last.kind === "voice") last.questions.push(step);
            else stages.push({ kind: "voice", questions: [step] });
        } else if (isFormQuestion(step) || isContent(step)) {
            if (!page) page = { kind: "page", items: [], questions: [] };
            page.items.push(step);
            if (isFormQuestion(step)) page.questions.push(step);
        }
    }
    closePage();
    return stages;
}

// The text a question is spoken and captioned as. The builder stores HTML, so
// block ends become spaces (or "<p>a</p><p>b</p>" reads "ab"), and entities are
// decoded (or "&amp;" is read aloud). Whitespace collapsed.
export const questionText = (step) => {
    const html = String(step?.question || "").replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, " ");
    const text =
        typeof DOMParser !== "undefined"
            ? new DOMParser().parseFromString(html, "text/html").documentElement.textContent || ""
            : stripHtml(html);
    return text.replace(/\s+/g, " ").trim();
};

// A long text cut into pieces the TTS endpoint accepts (2000 chars), at sentence
// ends where it can, so a long content block is read aloud piece by piece.
export const SPEECH_CHUNK_CHARS = 1500;
export function speechChunks(text, max = SPEECH_CHUNK_CHARS) {
    const chunks = [];
    let current = "";
    const sentences = String(text || "").match(/[^.!?。！？]+[.!?。！？]*\s*/g) || [];
    for (const sentence of sentences) {
        for (let i = 0; i < sentence.length; i += max) {
            const part = sentence.slice(i, i + max);
            if (current && (current + part).length > max) {
                chunks.push(current.trim());
                current = "";
            }
            current += part;
        }
    }
    if (current.trim()) chunks.push(current.trim());
    return chunks;
}

// What a content block is read aloud as: its text, then its description.
export const contentText = (step) =>
    [questionText(step), questionText({ question: step?.description })].filter(Boolean).join(" ");

const isAnswered = (step, value) => {
    if (step.type === "MULTI_CHOICE") return Array.isArray(value) && value.length > 0;
    if (step.type === "OPEN_TEXT") return typeof value === "string" && value.trim().length > 0;
    return value !== undefined && value !== null && value !== "";
};

// One /surveys/<id>/save/<token>/ item, in the classic runner's shape — or null
// when the question has no answer yet. Choice and Likert values are option ids.
export function answerItem(step, value) {
    if (!step || !isAnswered(step, value)) return null;
    switch (step.type) {
        case "OPEN_TEXT":
            return { question_id: step.id, question_type: "open_text", text: value.trim() };
        case "MULTI_CHOICE":
            return { question_id: step.id, question_type: "multi_choice", choice_ids: value };
        case "LIKERT_SCALE":
            return { question_id: step.id, question_type: "likert_scale", choice_id: value };
        case "SINGLE_CHOICE":
            return { question_id: step.id, question_type: "single_choice", choice_id: value };
        default:
            return null;
    }
}

export const answerItems = (steps, answers) =>
    (steps || []).map((step) => answerItem(step, (answers || {})[step.id])).filter(Boolean);

// Ids of the REQUIRED questions among `steps` that have no answer, in order.
export const missingRequired = (steps, answers) =>
    (steps || []).filter((step) => step.is_required && !isAnswered(step, (answers || {})[step.id])).map((step) => step.id);
